import React, { useState } from "react";
import { Search, Filter, X, ChevronDown, Check } from "lucide-react";
import { Severity } from "./SeverityBadge";

export interface FilterState {
  search: string;
  severities: Severity[];
  tools: string[];
  kinds: string[];
  statuses: ("open" | "fixed" | "suppressed")[];
  hasCve?: boolean;
  isKev?: boolean;
  minPriority?: number;
}

interface FilterBarProps {
  filters: FilterState;
  onChange: (filters: FilterState) => void;
  availableTools?: string[];
  totalCount?: number;
  filteredCount?: number;
}

const severityOptions: { value: Severity; label: string }[] = [
  { value: "critical", label: "Critical" },
  { value: "high", label: "High" },
  { value: "medium", label: "Medium" },
  { value: "low", label: "Low" },
  { value: "info", label: "Info" },
];

const kindOptions = [
  { value: "sast", label: "SAST" },
  { value: "sca", label: "Dependencies" },
  { value: "secret", label: "Secrets" },
  { value: "iac", label: "IaC" },
  { value: "license", label: "License" },
  { value: "container", label: "Container" },
];

const statusOptions = [
  { value: "open", label: "Open" },
  { value: "fixed", label: "Fixed" },
  { value: "suppressed", label: "Suppressed" },
];

export default function FilterBar({
  filters,
  onChange,
  availableTools = [],
  totalCount,
  filteredCount,
}: FilterBarProps) {
  const [showFilters, setShowFilters] = useState(false);

  const hasActiveFilters =
    filters.severities.length > 0 ||
    filters.kinds.length > 0 ||
    filters.statuses.length > 0 ||
    filters.hasCve ||
    filters.isKev;

  const clearFilters = () => {
    onChange({
      search: filters.search,
      severities: [],
      tools: [],
      kinds: [],
      statuses: [],
      hasCve: undefined,
      isKev: undefined,
      minPriority: undefined,
    });
  };

  const toggleSeverity = (sev: Severity) => {
    const newSeverities = filters.severities.includes(sev)
      ? filters.severities.filter((s) => s !== sev)
      : [...filters.severities, sev];
    onChange({ ...filters, severities: newSeverities });
  };

  const toggleKind = (kind: string) => {
    const newKinds = filters.kinds.includes(kind)
      ? filters.kinds.filter((k) => k !== kind)
      : [...filters.kinds, kind];
    onChange({ ...filters, kinds: newKinds });
  };

  const toggleStatus = (status: "open" | "fixed" | "suppressed") => {
    const newStatuses = filters.statuses.includes(status)
      ? filters.statuses.filter((s) => s !== status)
      : [...filters.statuses, status];
    onChange({ ...filters, statuses: newStatuses });
  };

  return (
    <div className="space-y-3">
      {/* Main bar */}
      <div className="flex flex-wrap items-center gap-3">
        {/* Search */}
        <div className="relative flex-1 min-w-[200px] max-w-md">
          <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted" />
          <input
            type="text"
            placeholder="Search findings..."
            value={filters.search}
            onChange={(e) => onChange({ ...filters, search: e.target.value })}
            className="input pl-10 py-2.5"
          />
          {filters.search && (
            <button
              onClick={() => onChange({ ...filters, search: "" })}
              className="absolute right-3 top-1/2 -translate-y-1/2 btn-icon h-6 w-6"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          )}
        </div>

        {/* Quick severity filters */}
        <div className="hidden sm:flex items-center gap-1.5">
          {severityOptions.map((opt) => (
            <button
              key={opt.value}
              onClick={() => toggleSeverity(opt.value)}
              className={`px-3 py-1.5 rounded-full text-[11px] font-semibold tracking-wide transition-colors duration-150 ${
                filters.severities.includes(opt.value)
                  ? `badge-${opt.value}`
                  : "bg-surface2/50 text-muted hover:text-text"
              }`}
            >
              {opt.label}
            </button>
          ))}
        </div>

        {/* More filters button */}
        <button
          onClick={() => setShowFilters(!showFilters)}
          className={`btn-secondary py-2 ${
            hasActiveFilters ? "ring-1 ring-brand/30" : ""
          }`}
        >
          <Filter className="h-4 w-4" />
          Filters
          {hasActiveFilters && (
            <span className="ml-1 bg-brand text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full min-w-[20px] text-center">
              {filters.severities.length + filters.kinds.length + filters.statuses.length}
            </span>
          )}
        </button>

        {/* Clear filters */}
        {hasActiveFilters && (
          <button onClick={clearFilters} className="btn-ghost text-xs py-2">
            Clear all
          </button>
        )}

        {/* Count */}
        {totalCount !== undefined && (
          <div className="text-xs font-medium text-muted ml-auto tabular-nums">
            {filteredCount !== undefined && filteredCount !== totalCount
              ? `${filteredCount.toLocaleString()} of ${totalCount.toLocaleString()} findings`
              : `${totalCount.toLocaleString()} findings`}
          </div>
        )}
      </div>

      {/* Expanded filters */}
      {showFilters && (
        <div className="card p-4 grid gap-4 sm:grid-cols-2 lg:grid-cols-4 animate-fade-in">
          {/* Severity */}
          <div>
            <div className="text-xs font-semibold text-text2 mb-2.5">Severity</div>
            <div className="flex flex-wrap gap-1.5">
              {severityOptions.map((opt) => (
                <button
                  key={opt.value}
                  onClick={() => toggleSeverity(opt.value)}
                  className={`px-2.5 py-1 rounded-lg text-[11px] font-semibold transition-colors duration-150 ${
                    filters.severities.includes(opt.value)
                      ? `badge-${opt.value}`
                      : "bg-surface2/50 text-muted hover:text-text"
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>

          {/* Kind */}
          <div>
            <div className="text-xs font-semibold text-text2 mb-2.5">Type</div>
            <div className="flex flex-wrap gap-1.5">
              {kindOptions.map((opt) => (
                <button
                  key={opt.value}
                  onClick={() => toggleKind(opt.value)}
                  className={`px-2.5 py-1 rounded-lg text-[11px] font-semibold transition-colors duration-150 ${
                    filters.kinds.includes(opt.value)
                      ? "bg-brand/15 text-brand"
                      : "bg-surface2/50 text-muted hover:text-text"
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>

          {/* Status */}
          <div>
            <div className="text-xs font-semibold text-text2 mb-2.5">Status</div>
            <div className="flex flex-wrap gap-1.5">
              {statusOptions.map((opt) => (
                <button
                  key={opt.value}
                  onClick={() => toggleStatus(opt.value as "open" | "fixed" | "suppressed")}
                  className={`px-2.5 py-1 rounded-lg text-[11px] font-semibold transition-colors duration-150 ${
                    filters.statuses.includes(opt.value as "open" | "fixed" | "suppressed")
                      ? "bg-brand/15 text-brand"
                      : "bg-surface2/50 text-muted hover:text-text"
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>

          {/* CVE/KEV Filters */}
          <div>
            <div className="text-xs font-semibold text-text2 mb-2.5">Vulnerability Intel</div>
            <div className="flex flex-wrap gap-1.5">
              <button
                onClick={() => onChange({ ...filters, hasCve: filters.hasCve ? undefined : true })}
                className={`px-2.5 py-1 rounded-lg text-[11px] font-semibold transition-colors duration-150 ${
                  filters.hasCve
                    ? "bg-brand/15 text-brand"
                    : "bg-surface2/50 text-muted hover:text-text"
                }`}
              >
                With CVE
              </button>
              <button
                onClick={() => onChange({ ...filters, isKev: filters.isKev ? undefined : true })}
                className={`px-2.5 py-1 rounded-lg text-[11px] font-semibold transition-colors duration-150 ${
                  filters.isKev
                    ? "bg-brand/15 text-brand"
                    : "bg-surface2/50 text-muted hover:text-text"
                }`}
              >
                KEV (Exploited)
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
